import React, {
  useState,
  useEffect,
  useReducer,
  useRef,
  useCallback,
} from "react";
import {
  BrowserRouter,
  Navigate,
  Routes,
  Route,
  Outlet,
  useNavigate,
  useLocation,
} from "react-router-dom";
import MaterialAdminPage from "./components/admin/MaterialAdminPage";
import MaterialFormPage from "./components/admin/MaterialFormPage";
import UserAdminPage from "./components/admin/UserAdminPage";
import PricingSettings from "./components/admin/PricingSettings";
import RateCardBuilderPage from "./components/admin/RateCardBuilderPage";
import DashboardPage from "./components/DashboardPage";
import QuoteView from "./components/QuoteView";
import PurchasingSheet from "./components/PurchasingSheet";
import LoginForm from "./components/auth/LoginForm";
import RegisterForm from "./components/auth/RegisterForm";
import UserProfile from "./components/auth/UserProfile";
import VersionFooter from "./components/ui/VersionFooter";
import {
  ProtectedRoute,
  AdminRoute,
  PublicOnlyRoute,
} from "./components/auth/ProtectedRoute";
import { useAuth } from "./context/AuthContext";
import { ToastProvider, useToast } from "./context/ToastContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { isMsalConfigured } from "./services/msalAuthService";
import { isAzureAuthEnabled } from "./services/azureAuthService";
import {
  calculateCosts,
  getProductTypes,
  getMaterials,
  getJob,
  getJobVersion,
  getPricingSettings,
  createJob,
} from "./services/apiService";
import { initialState, formReducer } from "./state/formReducer";
import SessionResumeDialog from "./components/SessionResumeDialog";
import UnsavedChangesDialog, {
  useUnsavedChangesGuard,
} from "./components/UnsavedChangesDialog";
import {
  forgetActiveEstimate,
  readActiveEstimate,
  rememberActiveEstimate,
  resolveUserEmail,
} from "./utils/sessionHandoff";
import {
  WorkbenchProvider,
  useWorkbench,
} from "./components/workbench/WorkbenchContext";
import { AppChromeBar } from "./components/workbench/WorkbenchTopBar";
import JobsWorkbenchPage from "./components/workbench/JobsWorkbenchPage";
import RafterTailTrialPage from "./components/workbench/trial/RafterTailTrialPage";
import FeatureHighlightModal from "./components/ui/FeatureHighlightModal";
import { useFeatureHighlights } from "./hooks/useFeatureHighlights";
import {
  alignLineItemResults,
  buildCalculationPayload,
  buildPersistedJobPayload,
  buildVersionResults,
  DEFAULT_NEW_ESTIMATE_CHANGE_NOTE,
} from "./utils/jobPayload";
import {
  adoptableLinkedRates,
  useAdoptLinkedRates,
} from "./components/workbench/estimate/rafterTailModel";
import "./index.css";
import "./App.css"; // Make sure this line exists

// ── Application chrome ──────────────────────────────────────────────────────
// Screens that render a <WorkbenchShell> bring their own 46px top bar. This
// Layout owns only what wraps a route: the estimator-view banner, a fallback
// chrome bar for non-Workbench routes, the version footer, and the
// "What's New" modal.
const ROUTE_TITLES = {
  "/profile": "Profile",
  "/admin/materials": "Materials",
  "/admin/users": "Users",
  "/admin/pricing-settings": "Pricing Settings",
  "/admin/rate-cards": "Rate Cards",
};

const Layout = ({ guidedIntakeOpen = false }) => {
  const { viewAsEstimator, toggleViewMode } = useAuth() || {};
  const { showModal, newFeatures, dismissHighlights } = useFeatureHighlights();
  const { shellCount } = useWorkbench();
  const location = useLocation();

  // No shell mounted on this route → render the standalone chrome so the
  // brand, destinations, theme cycler and account menu stay reachable.
  const needsFallbackChrome = shellCount === 0;

  return (
    <div className="app-shell-root">
      {viewAsEstimator && (
        <div className="app-estimator-banner">
          <span>You are viewing the app as an Estimator</span>
          <button type="button" onClick={toggleViewMode}>
            Exit preview
          </button>
        </div>
      )}

      {needsFallbackChrome ? (
        <AppChromeBar title={ROUTE_TITLES[location.pathname] || ""} />
      ) : null}

      <div className="app-shell-content">
        <Outlet />
      </div>

      {/* A shell route already has a bottom bar — the status strip, which is
          the job's bottom line. A second bar under it repeating a copyright
          on every screen is a row of chrome the estimate does not get back.
          On a shell route the build reads out of the account menu instead. */}
      {needsFallbackChrome ? <VersionFooter variant="compact" /> : null}

      <FeatureHighlightModal
        isOpen={showModal && !guidedIntakeOpen}
        newFeatures={newFeatures}
        onDismiss={dismissHighlights}
      />
    </div>
  );
};

// App component wrapped with AuthProvider
function AppContent() {
  const { theme } = useTheme(); // Ensure useTheme is called
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin, isAuthenticated, currentUser } = useAuth();
  const microsoftAuthOnly = isMsalConfigured() || isAzureAuthEnabled();
  // AuthContext publishes the signed-in person as `currentUser`. Destructuring
  // `user` here silently produced `undefined`, so every handoff marker was
  // written with `userEmail: null` and the resume prompt could never name who
  // left the estimate open — the one thing it exists to say.
  const sessionUserEmail = resolveUserEmail(currentUser);
  const [state, dispatch] = useReducer(formReducer, initialState);

  /**
   * The priced view.
   *
   * `results` is this component's state, but it is seeded from — and written
   * through to — the workbench store, which lives above <Routes>. Without that
   * the derived view (rail amounts, status strip, rate-card panel) only lasts
   * as long as this component instance; anything that remounts it drops the
   * prices while the inputs survive, and the estimator has to press Calculate
   * again to get numbers that were never wrong.
   *
   * Deliberately NOT an auto-recalculate on mount: re-running the calculator
   * because a screen was re-created would hide the problem behind a network
   * call and re-price the job on a route change.
   */
  const { lastResults, setLastResults, setSelectedLineIndex, setTraceScope } =
    useWorkbench();
  const [results, setResultsState] = useState(lastResults);
  const setResults = useCallback(
    (next) => {
      setResultsState(next);
      // Remember successes; a transient null (mid-calculation) must not wipe
      // the copy that survives navigation, but an explicit reset does.
      if (next) setLastResults(next);
    },
    [setLastResults],
  );
  const [calculationError, setCalculationError] = useState("");

  // Rafter-tail trial: keep the quote's Distributor rates (see the hook).
  useAdoptLinkedRates(results, state.linked_rates, dispatch);
  const [isCalculating, setIsCalculating] = useState(false);

  // Job management states
  const [currentJob, setCurrentJob] = useState(null);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [viewingVersionNumber, setViewingVersionNumber] = useState(null); // New state for tracking viewed version
  const [showWorkflowLauncher, setShowWorkflowLauncher] = useState(false);
  const [workflowLauncherError, setWorkflowLauncherError] = useState("");
  const [isCreatingEstimate, setIsCreatingEstimate] = useState(false);
  const hasPromptedForWorkflowRef = useRef(false);

  /* ── Whose estimate is this? ─────────────────────────────────────────────
     The session lives in localStorage, which every tab of the browser PROFILE
     shares — so a fresh tab silently lands inside whoever was last working
     here, with their job as the save target. The marker below records only
     which estimate was open (id, name, who, when); nothing is loaded from it
     until the estimator presses Resume. Read once, on mount, so it is a
     question asked at the door rather than a background restore.            */
  const [pendingResume, setPendingResume] = useState(() =>
    readActiveEstimate(),
  );
  const [isResumingSession, setIsResumingSession] = useState(false);

  const fetchCatalogData = useCallback(
    async ({ includePricing = false, applyPricingToEstimate = false } = {}) => {
      dispatch({ type: "SET_MATERIALS_LOADING", payload: true });

      try {
        const requests = [
          getMaterials(),
          // Product types (rate-card registry) — the only Product path now.
          // Non-fatal if it fails; typed lines just won't appear in the dropdown.
          getProductTypes().catch((err) => {
            console.warn("Failed to load product types:", err);
            return null;
          }),
        ];
        if (includePricing) {
          requests.push(
            // Non-fatal for materials/product types: resolve to a sentinel so a
            // pricing failure can't reject the whole batch and blank the form.
            getPricingSettings().catch((err) => {
              console.error("Failed to load pricing settings:", err);
              return { __loadFailed: true };
            }),
          );
        }

        const [materialsData, productTypesData, pricingSettings] =
          await Promise.all(requests);

        dispatch({ type: "SET_MATERIALS", payload: materialsData || [] });
        dispatch({
          type: "SET_PRODUCT_TYPES",
          payload: productTypesData?.product_types || [],
        });

        if (pricingSettings?.__loadFailed) {
          dispatch({
            type: "SET_MATERIALS_ERROR",
            payload:
              "Pricing settings failed to load — estimates would use stale factors. Refresh or sign in again.",
          });
        } else if (pricingSettings) {
          dispatch({
            type: "UPDATE_PRICING_SETTINGS",
            payload: {
              // A settings row saved before the selector was retired may still
              // spell a legacy mode; anything missing now means burden — the
              // shop labor cost rate — never the stale $30 custom fallback.
              laborRateType: pricingSettings.labor_rate_type || "burden",
              labor_custom_rate: pricingSettings.labor_custom_rate,
              labor_burden_rate: pricingSettings.labor_burden_rate,
              island_millwork_labor_rate:
                pricingSettings.island_millwork_labor_rate,
              default_client_pricing_tier:
                pricingSettings.default_client_pricing_tier || "retail",
              pricing_templates: pricingSettings.pricing_templates,
              applyToEstimate: applyPricingToEstimate,
            },
          });
        }
      } catch (error) {
        console.error("Failed to fetch catalog data:", error);
        dispatch({
          type: "SET_MATERIALS_ERROR",
          payload: error.message || "Failed to load materials",
        });
      } finally {
        dispatch({ type: "SET_MATERIALS_LOADING", payload: false });
      }
    },
    [],
  );

  const hasLoadedInitialCatalog = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    if (!hasLoadedInitialCatalog.current) {
      hasLoadedInitialCatalog.current = true;
      fetchCatalogData({
        includePricing: true,
        applyPricingToEstimate: true,
      });
      return;
    }

    // Refresh products/materials (and pricing settings) whenever returning
    // to dashboard so newly created admin records and pricing updates appear.
    if (location.pathname === "/") {
      fetchCatalogData({
        includePricing: true,
        applyPricingToEstimate: false,
      });
    }
  }, [isAuthenticated, location.pathname, fetchCatalogData]);

  useEffect(() => {
    if (
      isAuthenticated &&
      location.pathname === "/" &&
      !currentJob?.id &&
      !hasPromptedForWorkflowRef.current &&
      !state.materialsLoading &&
      // One question at a time: resume-or-fresh is answered first.
      !pendingResume
    ) {
      hasPromptedForWorkflowRef.current = true;
      setShowWorkflowLauncher(true);
    }
  }, [
    currentJob,
    isAuthenticated,
    location.pathname,
    state.materialsLoading,
    pendingResume,
  ]);

  const [resultsStale, setResultsStale] = useState(false);
  const lastCalculatedStateRef = useRef(null);
  const lastAutoAttemptSnapshotRef = useRef(null);
  const autoRecalcTimerRef = useRef(null);
  const calcGenerationRef = useRef(0);
  const AUTO_RECALC_DEBOUNCE_MS = 500;
  // Sentinel for lastCalculatedStateRef: truthy (so "has calculated before"
  // still holds) and never equal to any real snapshot.
  const LINE_SHAPE_CHANGED = "__line_shape_changed__";

  /* What gets priced. Excludes AI drafts the estimator has not accepted —
     see buildCalculationPayload for why that lives at the payload boundary. */
  const getCalculationPayload = useCallback(
    (formState) => buildCalculationPayload(formState),
    [],
  );

  const getCalculationSnapshot = useCallback(
    (formState) => JSON.stringify(getCalculationPayload(formState)),
    [getCalculationPayload],
  );

  /* What counts as unsaved work. Everything the form holds, pending AI drafts
     included — an unaccepted draft is still work you would hate to lose, so
     the dirty test must see it even though the pricing payload does not. */
  const getDirtySnapshot = useCallback(
    (formState) => JSON.stringify(buildPersistedJobPayload(formState)),
    [],
  );

  useEffect(() => {
    return () => {
      if (autoRecalcTimerRef.current) {
        clearTimeout(autoRecalcTimerRef.current);
      }
    };
  }, []);

  const handleCalculate = async () => {
    if (autoRecalcTimerRef.current) {
      clearTimeout(autoRecalcTimerRef.current);
    }
    setCalculationError("");
    setResults(null);
    setIsCalculating(true);
    try {
      const dataToSend = getCalculationPayload(state);
      // Pending drafts were held out of the payload, so the results come back
      // one row short — realign before anything indexes into them.
      const calcGeneration = calcGenerationRef.current;
      const response = await calculateCosts(dataToSend);
      if (calcGeneration !== calcGenerationRef.current) return null;
      const calculatedResults = alignLineItemResults(
        response,
        state.line_items,
      );
      setResults(calculatedResults);
      setResultsStale(false); // Mark results as fresh
      lastCalculatedStateRef.current = getCalculationSnapshot(state);
      return calculatedResults;
    } catch (apiError) {
      console.error("Calculation failed:", apiError);
      setCalculationError(
        apiError.errors
          ? JSON.stringify(apiError.errors)
          : apiError.message || "Failed to calculate costs.",
      );
      // A failed run must not leave a survivable copy behind — the numbers on
      // screen and the numbers that come back after a route change must agree.
      setResults(null);
      setLastResults(null);
      return null;
    } finally {
      setIsCalculating(false);
    }
  };

  // Keep stale state in sync and auto-recalculate when estimate inputs change.
  useEffect(() => {
    if (isCalculating) {
      return;
    }

    const hasPricingInput = state.line_items.some(
      (line) =>
        Boolean(line?.product_type_key || line?.product_id) ||
        (line?.assembly_components || []).length > 0 ||
        (line?.children || []).length > 0,
    );
    if (!results && !hasPricingInput) {
      return;
    }

    const currentSnapshot = getCalculationSnapshot(state);
    const hasCalculatedBefore = Boolean(lastCalculatedStateRef.current);
    const isOutdated =
      (!results &&
        hasPricingInput &&
        currentSnapshot !== lastAutoAttemptSnapshotRef.current) ||
      (hasCalculatedBefore &&
        currentSnapshot !== lastCalculatedStateRef.current);

    if (!isOutdated) {
      setResultsStale(false);
      return;
    }

    setResultsStale(true);
    if (autoRecalcTimerRef.current) {
      clearTimeout(autoRecalcTimerRef.current);
    }

    autoRecalcTimerRef.current = setTimeout(async () => {
      lastAutoAttemptSnapshotRef.current = currentSnapshot;
      try {
        setCalculationError("");
        setIsCalculating(true);
        const calcGeneration = calcGenerationRef.current;
        const response = await calculateCosts(getCalculationPayload(state));
        if (calcGeneration !== calcGenerationRef.current) return;
        const recalculated = alignLineItemResults(response, state.line_items);
        setResults(recalculated);
        setResultsStale(false);
        lastCalculatedStateRef.current = getCalculationSnapshot(state);
      } catch (apiError) {
        console.error("Auto recalculation failed:", apiError);
        setCalculationError(
          apiError.errors
            ? JSON.stringify(apiError.errors)
            : apiError.message || "Failed to auto-recalculate costs.",
        );
      } finally {
        setIsCalculating(false);
      }
    }, AUTO_RECALC_DEBOUNCE_MS);

    return () => {
      if (autoRecalcTimerRef.current) {
        clearTimeout(autoRecalcTimerRef.current);
      }
    };
  }, [
    state,
    results,
    isCalculating,
    setResults,
    getCalculationPayload,
    getCalculationSnapshot,
  ]);

  const lastLineItemsRevisionRef = useRef(state.line_items_revision || 0);

  /* Per-line figures are matched to lines by POSITION. Removing or duplicating
     a line shifts the array, so for the whole 500 ms debounce plus the network
     round trip above, line 2 shows line 3's money and the last row shows money
     for a line that no longer exists. In a quoting tool a confidently wrong
     price is worse than no price, so the per-line figures are dropped the
     instant the shape changes; the job-level totals stay, visibly stale, and
     the recalculation puts everything back a moment later.

     The reducer's counter is what "the shape changed" means here — it moves
     only on position-shifting edits, and a job/version load carries it over, so
     this never clears figures a load just set. The ref makes the mount run (and
     every re-run for an unrelated dependency) a no-op.

     The durable fix is stable line IDs threaded through the calculate payload
     and the results, so a result can name its line instead of being counted to
     — required before any reorder feature lands. It is on the backlog. */
  useEffect(() => {
    const rev = state.line_items_revision || 0;
    if (rev === lastLineItemsRevisionRef.current) return;
    lastLineItemsRevisionRef.current = rev;
    // A response for the old positional shape must never restore its figures.
    calcGenerationRef.current += 1;
    // Nothing priced yet means nothing to drop and nothing to call stale — a
    // "Recalculate" hint on an empty job would be the watcher talking to itself.
    if (!results) return;
    if (
      Array.isArray(results.line_item_results) &&
      results.line_item_results.length
    ) {
      setResults({ ...results, line_item_results: [] });
    }
    setResultsStale(true);
    // The figures just dropped have to come back, and the auto-recalculate
    // above decides that by CONTENT: add a blank line, remove it again before
    // the 500 ms run fires, and the form is byte-for-byte what was last
    // priced — so it would call the job fresh while every line reads "—".
    // A shape change is a change in what was priced, whatever the text says.
    lastCalculatedStateRef.current = LINE_SHAPE_CHANGED;
  }, [state.line_items_revision, results, setResults]);

  const toast = useToast();

  /* ── Unsaved-work guard ──────────────────────────────────────────────────
     An estimate lives in memory until Save writes a JobVersion. Closing the
     tab on a job that has been touched since the last save throws the work
     away with no way back, so the browser is asked to confirm first. The
     dirty test is the same snapshot the stale-results detector uses (form
     inputs only — catalog data and loading flags are stripped), compared
     against the state as of the last save/load rather than the last
     calculation.                                                            */
  const lastSavedStateRef = useRef(null);

  /* The one dirty test. The tab-close guard asks it, and so does every in-app
     door that replaces the form wholesale (New, opening another job, loading a
     version) — one answer, so the three doors can never disagree with the
     browser's own warning. */
  const isFormDirty = useCallback(() => {
    const saved = lastSavedStateRef.current;
    if (saved) return getDirtySnapshot(state) !== saved;
    // Never saved: the baseline is what a FRESH form holds under the current
    // settings and catalog — not the static initialState. Boot-time
    // UPDATE_PRICING_SETTINGS rewrites persisted fields (rates, factors, the
    // resolved pricing tier) and RESET_FORM does the same, so a pristine form
    // never equals initialState once the app has loaded. RESET_FORM is the one
    // place that knows what "untouched" looks like right now; ask it.
    const fresh = formReducer(state, { type: "RESET_FORM" });
    return getDirtySnapshot(state) !== getDirtySnapshot(fresh);
  }, [state, getDirtySnapshot]);

  /* A guarded action runs AFTER its caller has been told "nothing happened"
     (see useUnsavedChangesGuard) — so a load that fails once the estimator has
     picked Discard has no caller left to report to. handleJobSelect already
     toasts the specific error; this is the backstop for anything that escapes
     it, so a discarded estimate never turns into a silent no-op. */
  const { guard: guardUnsavedChanges, dialogProps: unsavedChangesDialogProps } =
    useUnsavedChangesGuard(isFormDirty, {
      onError: (error) => {
        console.error("Failed to continue after discarding changes:", error);
        toast.error("Couldn't open that estimate.", { title: "Load failed" });
      },
    });
  /* What a loaded job's state looks like once the app has finished with it.

     Opening a job is not the last write to the form: returning to "/" re-runs
     the catalog/pricing-settings refresh, and its UPDATE_PRICING_SETTINGS
     (applyToEstimate:false) fills whatever the saved job_data did not carry —
     laborRateType, labor_custom_rate, the tier default — from the live
     settings. A version saved before those fields existed therefore moves
     AFTER the dirty baseline was taken, and reads as unsaved work the
     estimator never typed. So the baseline is taken from the SETTLED state:
     the loaded form with the same settings pass applied, built from the
     settings already in state (what that refresh will resolve to). */
  /* The form state as of the latest render. Handlers that await network
     calls (handleJobSelect: two fetches) must not build their baseline from
     the `state` they closed over — on a fresh boot the resume click fires
     while the catalog and pricing-settings fetches are still in flight, those
     dispatches land during the awaits, and a snapshot taken from the stale
     closure then disagrees with the reducer's real output. Read this ref
     after the awaits instead. */
  const latestStateRef = useRef(state);
  latestStateRef.current = state;

  /* The settings pass a loaded job gets. The "/" refresh applies exactly this
     (UPDATE_PRICING_SETTINGS, applyToEstimate:false) — but only after a ROUTE
     CHANGE, so Jobs → Open got it and a resume that starts on "/" did not.
     Two paths, two shapes of the same job (string "36" vs 36, laborRateType
     present or missing), and any baseline that guessed one of them was wrong
     on the other. So the load applies the pass itself, right after
     SET_FORM_DATA, and the baseline is taken from the same output: the form
     is settled the moment it lands, on every path, and the refresh that may
     follow is a no-op. */
  const liveSettingsPass = useCallback(() => {
    const live = latestStateRef.current;
    return {
      type: "UPDATE_PRICING_SETTINGS",
      payload: {
        laborRateType: live.laborRateType,
        labor_custom_rate: live.labor_custom_rate,
        labor_burden_rate: live.labor_burden_rate,
        island_millwork_labor_rate: live.island_millwork_labor_rate,
        default_client_pricing_tier: live.default_client_pricing_tier,
        pricing_templates: live.pricing_templates,
        applyToEstimate: false,
      },
    };
  }, []);

  const settleLoadedState = useCallback(
    (loaded) => formReducer(loaded, liveSettingsPass()),
    [liveSettingsPass],
  );

  useEffect(() => {
    const guard = (event) => {
      if (!isFormDirty()) return undefined;
      event.preventDefault();
      // Legacy browsers need returnValue set; the string itself is ignored.
      event.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [isFormDirty]);

  const handleJobSaved = (savedJob) => {
    setCurrentJob(savedJob);
    rememberActiveEstimate({
      jobId: savedJob.id,
      jobName: savedJob.job_name,
      userEmail: sessionUserEmail,
    });
    // When a job is saved (new or update), we are viewing the latest version
    setViewingVersionNumber(savedJob.version_count);
    // The server records the quote's Distributor rates on its first save
    // (snapshot_linked_rates). Adopt them before taking the saved baseline, so
    // a quote saved before its first calculation returned does not read as
    // unsaved the moment the rates land.
    const adoptedRates = adoptableLinkedRates(
      savedJob.calculation_results,
      state.linked_rates,
    );
    const savedState = adoptedRates
      ? { ...state, linked_rates: adoptedRates }
      : state;
    if (adoptedRates) {
      dispatch({
        type: "UPDATE_FIELD",
        field: "linked_rates",
        value: adoptedRates,
      });
    }
    lastSavedStateRef.current = getDirtySnapshot(savedState);

    /* Save re-prices on the server and stores the result on the new
       JobVersion, but the screen used to keep whatever it had — so the numbers
       under the estimator's eyes could differ from the numbers now on record,
       with nothing saying which was the quote. Adopt the version's own figures.

       Aligned to the form's rows first: the save payload PERSISTS every line,
       pending AI drafts included, but the backend (costable_job_data in
       job_routes.py) holds unaccepted drafts out of the pricing run — so the
       returned line_item_results is one entry shorter per pending draft, and
       used as-is it would slide each later row's money onto its neighbour.
       Same treatment buildVersionResults gives these results when the version
       is reopened. */
    if (savedJob.calculation_results) {
      setResults(
        alignLineItemResults(savedJob.calculation_results, state.line_items),
      );
      setResultsStale(false);
      lastCalculatedStateRef.current = getCalculationSnapshot(savedState);
    }

    toast.success(`Job "${savedJob.job_name}" saved successfully!`, {
      title: `Version ${savedJob.version_count} created`,
    });
  };

  const handleCreateEstimate = async (jobDetails) => {
    if (!isAuthenticated) {
      setWorkflowLauncherError("Please log in to create estimates.");
      return;
    }

    if (!jobDetails?.job_name?.trim()) {
      setWorkflowLauncherError("Job Name is required.");
      return;
    }

    setIsCreatingEstimate(true);
    setWorkflowLauncherError("");

    const trimmedJobDetails = {
      ...jobDetails,
      job_name: jobDetails.job_name.trim(),
      customer_name: jobDetails.customer_name?.trim() || "",
    };
    const nextState = formReducer(state, {
      type: "START_ESTIMATE",
      payload: trimmedJobDetails,
    });

    try {
      const payload = {
        ...buildPersistedJobPayload(nextState),
        change_note: DEFAULT_NEW_ESTIMATE_CHANGE_NOTE,
      };
      const savedJob = await createJob(payload);

      dispatch({ type: "START_ESTIMATE", payload: trimmedJobDetails });
      setCurrentJob(savedJob);
      rememberActiveEstimate({
        jobId: savedJob.id,
        jobName: savedJob.job_name,
        userEmail: sessionUserEmail,
      });
      setViewingVersionNumber(savedJob.version_count);
      const firstLine = nextState.line_items[0];
      const hasInitialPricingInput =
        Boolean(firstLine?.product_type_key || firstLine?.product_id) ||
        (firstLine?.assembly_components || []).length > 0 ||
        (firstLine?.children || []).length > 0;
      setResults(
        hasInitialPricingInput
          ? alignLineItemResults(
              savedJob.calculation_results || null,
              nextState.line_items,
            )
          : null,
      );
      if (!hasInitialPricingInput) setLastResults(null);
      setResultsStale(false);
      lastCalculatedStateRef.current = hasInitialPricingInput
        ? getCalculationSnapshot(nextState)
        : null;
      lastAutoAttemptSnapshotRef.current = null;
      lastSavedStateRef.current = getDirtySnapshot(nextState);
      setSelectedLineIndex(0);
      setTraceScope("line");
      setShowWorkflowLauncher(false);
      toast.success(`Job "${savedJob.job_name}" created successfully!`, {
        title: `Version ${savedJob.version_count} created`,
      });
    } catch (error) {
      console.error("Failed to create estimate:", error);
      setWorkflowLauncherError(
        error.message || error.error || "Failed to create estimate.",
      );
    } finally {
      setIsCreatingEstimate(false);
    }
  };

  const handleNewJob = () => {
    dispatch({ type: "RESET_FORM" });
    setCurrentJob(null);
    // The job just left is not this form's save target any more; a fresh form
    // is judged against RESET_FORM's output, not the old job's snapshot.
    lastSavedStateRef.current = null;
    // Starting over must not leave a marker pointing at the job just left.
    forgetActiveEstimate();
    setPendingResume(null);
    setResults(null);
    setLastResults(null);
    setResultsStale(false);
    lastSavedStateRef.current = null;
    setViewingVersionNumber(null); // Reset viewing version
    setWorkflowLauncherError("");
    setShowWorkflowLauncher(true);
  };

  /**
   * Open a saved job. Both fetches complete — AND the whole next state is
   * computed — BEFORE a single piece of state moves.
   *
   * The save target (`currentJob`) and the form contents have to change
   * together or not at all: committing the target first and then failing to
   * load its version leaves the screen showing job A while Save writes to job
   * B — an overwrite of the wrong estimate, from a failure the user only saw
   * as a toast. That guarantee is not just about the fetches: building the
   * results after the first setter (mapping `job_data.line_items` on a version
   * that has none) could throw halfway through and land in exactly the same
   * split state, with the re-throw sending the caller back to /jobs as though
   * nothing had happened. `buildVersionResults` does that work up front, so
   * nothing after the first commit can fail. On failure nothing is touched and
   * the error is re-thrown, so the resume path can forget a session it could
   * not restore.
   */
  const handleJobSelect = async (job) => {
    try {
      const jobData = await getJob(job.id);
      const version = await getJobVersion(job.id, jobData.current_version_id);
      const { results: nextResults, fromCalculation } =
        buildVersionResults(version);

      // Both loads landed and the next state is in hand — from here on the
      // commit cannot half-happen.
      setCurrentJob(jobData);
      rememberActiveEstimate({
        jobId: jobData.id,
        jobName: jobData.job_name,
        userEmail: sessionUserEmail,
      });

      // SET_FORM_DATA normalizes what it is given (line items hydrated,
      // pricing fields filled, a tier defaulted), so the snapshot has to be
      // taken from the reducer's own output — snapshotting the raw job_data
      // left every freshly opened job reading as unsaved work. And it has to
      // start from the state the reducer will actually see: two awaits have
      // passed since this handler's `state` was captured, and on a fresh boot
      // the catalog/pricing dispatches land inside that window (a resume click
      // races them). The stale closure produced a baseline the live reducer
      // never matched, and every resumed job opened "dirty".
      const loadedState = formReducer(latestStateRef.current, {
        type: "SET_FORM_DATA",
        payload: version.job_data,
      });
      const settledState = settleLoadedState(loadedState);
      dispatch({ type: "SET_FORM_DATA", payload: version.job_data });
      dispatch(liveSettingsPass());
      setViewingVersionNumber(version.version_number); // Set viewing version to the latest

      setResults(nextResults);
      lastSavedStateRef.current = getDirtySnapshot(settledState);
      if (fromCalculation) {
        // Save snapshot so stale detection works — from the settled state,
        // or the settings pass itself would read as "inputs changed".
        setResultsStale(false);
        lastCalculatedStateRef.current = getCalculationSnapshot(settledState);
      }

      setSelectedLineIndex(0);
      setTraceScope("line");
    } catch (error) {
      console.error("Failed to load job:", error);
      toast.error(`Error loading job: ${error.message}`);
      // Re-thrown so handleResumeSession can forget an estimate it failed to
      // restore rather than leaving the user with a resume prompt that never
      // works.
      throw error;
    }
  };

  /* Resume-or-fresh. Resume goes through the ordinary job-open path — there is
     no second loader and no restored client state, so a resumed estimate is
     byte-for-byte the saved JobVersion. Start fresh only forgets the marker;
     the saved job itself is untouched and still in Jobs. */
  const handleResumeSession = async () => {
    if (!pendingResume) return;
    setIsResumingSession(true);
    try {
      await handleJobSelect({ id: pendingResume.jobId });
      setPendingResume(null);
    } catch (error) {
      console.error("Failed to resume estimate:", error);
      forgetActiveEstimate();
      setPendingResume(null);
    } finally {
      setIsResumingSession(false);
    }
  };

  const handleStartFreshSession = () => {
    forgetActiveEstimate();
    setPendingResume(null);
  };

  const handleVersionSelect = (version) => {
    // Set the form inputs to match the state when this version was saved.
    // The snapshot comes from the reducer's output, not the raw job_data —
    // SET_FORM_DATA normalizes, so the raw payload never matches the state it
    // produces and the loaded version would read as unsaved work.
    const loadedState = formReducer(latestStateRef.current, {
      type: "SET_FORM_DATA",
      payload: version.job_data,
    });
    const settledState = settleLoadedState(loadedState);
    dispatch({ type: "SET_FORM_DATA", payload: version.job_data });
    dispatch(liveSettingsPass());
    setViewingVersionNumber(version.version_number); // Set viewing version to the selected one
    lastSavedStateRef.current = getDirtySnapshot(settledState);

    // Use the stored calculation results from the version object. The backend
    // converts Decimals to strings, so the cost trace parses every figure
    // rather than assuming a number.
    if (version.calculation_results) {
      setResults(
        alignLineItemResults(
          version.calculation_results,
          version.job_data?.line_items,
        ),
      );
      // Save snapshot so stale detection works
      setResultsStale(false);
      lastCalculatedStateRef.current = getCalculationSnapshot(settledState);
    } else {
      // Fallback if calculation_results is missing (e.g., for older versions before the field was added)
      // Reconstruct results from individual fields if possible
      console.warn(
        `Version ${version.version_number} is missing calculation_results. Attempting fallback.`,
      );
      setResults({
        // Map line item data if available in job_data
        line_item_results:
          version.job_data?.line_items?.map((item) => ({
            ...item,
            // Attempt to find calculated costs if stored within job_data (less ideal)
            calculated_base_cost: item.calculated_base_cost || "0.00",
            calculated_labor_cost: item.calculated_labor_cost || "0.00",
            calculated_overhead_cost: item.calculated_overhead_cost || "0.00",
            calculated_total_cost: item.calculated_total_cost || "0.00",
          })) || [],
        // Use the individual total fields stored directly on the version
        total_base_cost: version.total_base_cost || "0.00",
        total_labor_cost: version.total_labor_cost || "0.00",
        total_line_item_cost: version.total_line_item_cost || "0.00",
        install_labor_cost: version.install_labor_cost || "0.00",
        delivery_cost: version.delivery_cost || "0.00",
        misc_costs: version.misc_costs || "0.00",
        grand_total: version.grand_total || "0.00",
        overhead_cost: version.overhead_cost || "0.00",
        break_even_cost: version.break_even_cost || "0.00",
        profit_amount: version.profit_amount || "0.00",
        final_quoted_price: version.final_quoted_price || "0.00",
        // Indicate that these results are potentially reconstructed
        isFallback: true,
      });
    }

    setShowVersionHistory(false);
    toast.info(`Form inputs and results now reflect this version.`, {
      title: `Version ${version.version_number} loaded`,
    });
  };

  /* The three doors that replace the form wholesale. Each keeps its own body
     exactly as it was; the guard only decides whether it runs now, after a
     confirm, or not at all.

     handleResumeSession deliberately calls the RAW handleJobSelect: a resume
     happens on a fresh boot, where the form is clean by definition, and it
     depends on the load's rejection propagating so it can forget an estimate
     it could not restore. */
  const guardedNewJob = guardUnsavedChanges(handleNewJob, {
    destination: "start a new estimate",
  });
  /* Opening from a list means "load it AND take me to it". The navigation
     lives here, behind the guard, rather than in the Jobs page after its
     await: a parked (dirty-form) call resolves immediately so the page never
     shows a pending state while the dialog asks — which also means the page
     cannot know whether the open actually happened. Only the action itself
     knows, so only the action navigates. On "/" it is a no-op. */
  const openJobFromList = async (job) => {
    await handleJobSelect(job);
    navigate("/");
  };
  const guardedJobSelect = guardUnsavedChanges(openJobFromList, {
    destination: (job) => `open ${job?.job_name || "that estimate"}`,
  });
  const guardedVersionSelect = guardUnsavedChanges(handleVersionSelect, {
    destination: (version) => `load version ${version?.version_number}`,
  });
  /* Same handler as opening a job — re-opening loads the job's current
     version — but from the estimator's seat it is not an "open": they are
     already in this job, looking at an old version of it. So the confirm names
     what actually happens. */
  const guardedLoadLatest = guardUnsavedChanges(handleJobSelect, {
    destination: (job) => `load version ${job?.version_count ?? "latest"}`,
  });

  // Display loading/error messages for initial data
  return (
    <div className={`App ${theme !== "light" ? `${theme}-theme` : ""}`}>
      {/* Asked once, at the door: continue the estimate this browser profile
          was left on, or start fresh. Only for a signed-in session, and never
          over a job that is already open in this tab. */}
      <SessionResumeDialog
        open={Boolean(pendingResume && isAuthenticated && !currentJob?.id)}
        marker={pendingResume}
        currentEmail={sessionUserEmail}
        isResuming={isResumingSession}
        onResume={handleResumeSession}
        onStartFresh={handleStartFreshSession}
      />
      {/* Asked before any door that would replace the form: New, opening
          another job, or loading an older version. */}
      <UnsavedChangesDialog {...unsavedChangesDialogProps} />
      <Routes>
        {/* Public routes that don't require authentication */}
        <Route element={<PublicOnlyRoute />}>
          <Route path="/login" element={<LoginForm />} />
          <Route
            path="/register"
            element={
              microsoftAuthOnly ? (
                <Navigate to="/login" replace />
              ) : (
                <RegisterForm />
              )
            }
          />
        </Route>

        {/* Layout wrapper for all authenticated routes */}
        <Route element={<ProtectedRoute />}>
          <Route
            path="/"
            element={<Layout guidedIntakeOpen={showWorkflowLauncher} />}
          >
            {/* Main Costing Form Route */}
            <Route
              index
              element={
                <DashboardPage
                  theme={theme}
                  state={state}
                  dispatch={dispatch}
                  currentJob={currentJob}
                  viewingVersionNumber={viewingVersionNumber}
                  onJobSaved={handleJobSaved}
                  onNewJobClick={guardedNewJob}
                  showVersionHistory={showVersionHistory}
                  setShowVersionHistory={setShowVersionHistory}
                  onVersionSelect={guardedVersionSelect}
                  onLoadLatest={
                    currentJob?.id
                      ? () => guardedLoadLatest(currentJob)
                      : undefined
                  }
                  onCalculate={handleCalculate}
                  isCalculating={isCalculating}
                  calculationError={calculationError}
                  results={results}
                  resultsStale={resultsStale}
                  showWorkflowLauncher={showWorkflowLauncher}
                  setShowWorkflowLauncher={setShowWorkflowLauncher}
                  workflowLauncherError={workflowLauncherError}
                  isCreatingEstimate={isCreatingEstimate}
                  onCreateEstimate={handleCreateEstimate}
                  onWorkflowNavigate={(path) => {
                    setShowWorkflowLauncher(false);
                    navigate(path);
                  }}
                  isAdmin={isAdmin}
                />
              }
            />

            {/* Saved jobs — the entry rail */}
            <Route
              path="jobs"
              element={
                <JobsWorkbenchPage
                  onJobSelect={guardedJobSelect}
                  currentJobId={currentJob?.id ?? null}
                />
              }
            />

            {/* Rafter-tail pricing trial: quote outcomes and the review. */}
            <Route path="trial" element={<RafterTailTrialPage />} />

            {/* The quote as a printable document — stored numbers only. */}
            <Route path="jobs/:jobId/quote" element={<QuoteView />} />
            {/* What to buy for the job's rafter tails — stored numbers only. */}
            <Route
              path="jobs/:jobId/order-sheet"
              element={<PurchasingSheet />}
            />

            {/* User Profile Route */}
            <Route path="/profile" element={<UserProfile />} />

            <Route element={<AdminRoute />}>
              <Route path="admin/materials" element={<MaterialAdminPage />} />
              <Route
                path="admin/materials/new"
                element={<MaterialFormPage />}
              />
              <Route
                path="admin/materials/:id/edit"
                element={<MaterialFormPage />}
              />
              <Route path="admin/users" element={<UserAdminPage />} />
              <Route
                path="admin/pricing-settings"
                element={<PricingSettings />}
              />
              <Route
                path="admin/rate-cards"
                element={<RateCardBuilderPage />}
              />
            </Route>
          </Route>
        </Route>
      </Routes>
    </div>
  );
}

// Wrap the app with the auth provider
function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <ToastProvider>
          {/* The workbench store sits ABOVE AppContent so the last priced view
              (and which pane/line is open) outlives every route change. */}
          <WorkbenchProvider>
            <AppContent />
          </WorkbenchProvider>
        </ToastProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
